/* Seven Thousand — LED Flag Matrix simulation engine.
   Drives an 8x8 grid of 64 child elements. Single source of truth: this file is
   loaded by the design component AND copied to shopify/assets/.

   Mirrors the firmware's real behaviour: distinct motion per flag state,
   gear-change takeover, pit indicators, gamma-corrected intensity and
   crossfaded state transitions. The timeline lives in the LIVE and RACE arrays
   further down. */
(function (global) {
  'use strict';

  var GAMMA = 2.2;          // LED gamma correction — even-looking gradients
  var CROSSFADE = 0.36;     // seconds; states never snap

  /* Pipeline: the palette below is authored in DISPLAY brightness (what the eye
     should see). toLinear() converts it to linear light, all blending happens in
     linear space, and the frame loop encodes back with pow(v, 1/GAMMA). A value
     written here therefore renders as itself, and gradients between values are
     photometrically even instead of crushed in the mids. */
  function toLinear(c) {
    return [Math.pow(c[0], GAMMA), Math.pow(c[1], GAMMA), Math.pow(c[2], GAMMA)];
  }

  var OFF = toLinear([0.055, 0.055, 0.065]);

  /* ---- palette (display brightness, 0..1) ---- */
  var C = {
    green:  [0.09, 0.72, 0.30],
    amber:  [0.92, 0.58, 0.10],
    gold:   [0.98, 0.72, 0.22],
    yellow: [0.86, 0.68, 0.08],
    red:    [0.78, 0.20, 0.07],
    deep:   [0.55, 0.03, 0.02],
    blue:   [0.12, 0.36, 0.80],
    white:  [0.95, 0.94, 0.92]
  };
  Object.keys(C).forEach(function (k) { C[k] = toLinear(C[k]); });

  function mix(a, b, t) {
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
  }
  /* k is a display-brightness multiplier, so it is gamma-shaped before being
     applied to linear light — otherwise a "dim floor" of 0.12 reads as ~45%. */
  function scale(c, k) {
    var g = Math.pow(k < 0 ? 0 : k, GAMMA);
    return [c[0] * g, c[1] * g, c[2] * g];
  }
  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

  /* ---- triple-chevron mark on an 8x8 grid ----
     Column depth profile; three chevrons stacked at rows 1, 3, 5. */
  var CHEV_DEPTH = [0, 0, 1, 2, 2, 1, 0, 0];
  var CHEVRON = (function () {
    var m = {};
    [1, 3, 5].forEach(function (top) {
      for (var c = 0; c < 8; c++) m[(top + CHEV_DEPTH[c]) * 8 + c] = true;
    });
    return m;
  })();

  /* ---- 6x6 glyphs for the gear takeover.
     6 wide x 6 tall, drawn at cols 1-6 / rows 1-6, so the digit is centred on
     the 8x8 grid on both axes with a one-pixel margin all round. ---- */
  var GLYPHS = {
    R: ['#####.', '#....#', '#####.', '#..#..', '#...#.', '#....#'],
    N: ['#....#', '##...#', '#.#..#', '#..#.#', '#...##', '#....#'],
    1: ['..##..', '.#.#..', '...#..', '...#..', '...#..', '.####.'],
    2: ['.####.', '#....#', '....#.', '..##..', '.#....', '######'],
    3: ['.####.', '#....#', '...##.', '.....#', '#....#', '.####.'],
    4: ['....#.', '...##.', '..#.#.', '.#..#.', '######', '....#.'],
    5: ['######', '#.....', '#####.', '.....#', '#....#', '.####.'],
    6: ['..###.', '.#....', '#.....', '#####.', '#....#', '.####.'],
    7: ['######', '.....#', '....#.', '...#..', '..#...', '.#....']
  };
  var GEAR_ORDER = ['R', 'N', '1', '2', '3', '4', '5', '6', '7'];

  /* ---- 6x6 glyphs for the gear takeover.
     6 wide x 6 tall, drawn at cols 1-6 / rows 1-6, so the digit is centred on
     the 8x8 grid on both axes with a one-pixel margin all round. ---- */
  var GLYPHS = {
    R: ['#####.', '#....#', '#####.', '#..#..', '#...#.', '#....#'],
    N: ['#....#', '##...#', '#.#..#', '#..#.#', '#...##', '#....#'],
    1: ['..##..', '.#.#..', '...#..', '...#..', '...#..', '.####.'],
    2: ['.####.', '#....#', '....#.', '..##..', '.#....', '######'],
    3: ['.####.', '#....#', '...##.', '.....#', '#....#', '.####.'],
    4: ['....#.', '...##.', '..#.#.', '.#..#.', '######', '....#.'],
    5: ['######', '#.....', '#####.', '.....#', '#....#', '.####.'],
    6: ['..###.', '.#....', '#.....', '#####.', '#....#', '.####.'],
    7: ['######', '.....#', '....#.', '...#..', '..#...', '.#....']
  };
  var GEAR_ORDER = ['R', 'N', '1', '2', '3', '4', '5', '6', '7'];

  function glyphHas(ch, r, c) {
    var g = GLYPHS[ch];
    if (!g) return false;
    var gc = c - 1, gr = r - 1;
    if (gc < 0 || gc > 5 || gr < 0 || gr > 5) return false;
    return g[gr].charAt(gc) === '#';
  }

  /* ---- effects: (t seconds into state, row, col, progress 0..1) -> linear rgb ---- */
  var EFFECTS = {
    dark: function () { return OFF; },

    /* warm amber-gold gradient wash rising up the matrix */
    swell: function (t, r, c, p) {
      var front = (1 - p) * 9 - 1;                     // travels from bottom edge upward
      var d = r - front;
      var v = clamp01(1 - Math.abs(d) / 3.2) * (0.35 + 0.65 * p);
      var tone = mix(C.amber, C.gold, clamp01((7 - r) / 7));
      return mix(OFF, scale(tone, 0.25 + 0.75 * v), clamp01(v * 1.6 + p * 0.25));
    },

    /* the triple-chevron mark reveals left to right */
    chevron: function (t, r, c, p) {
      var edge = p * 9.5 - 0.75;
      if (c > edge) return OFF;
      var lit = CHEVRON[r * 8 + c];
      var wipe = clamp01(1 - (edge - c) / 1.4);        // bright leading edge
      if (!lit) return mix(OFF, scale(C.amber, 0.10), wipe);
      return mix(scale(C.gold, 0.85), C.white, wipe * 0.5);
    },

    /* mark holds with a gentle pulse, waiting on engine start */
    hold: function (t, r, c) {
      var pulse = 0.72 + 0.28 * (0.5 + 0.5 * Math.sin(t * 2.1));
      if (!CHEVRON[r * 8 + c]) return mix(OFF, scale(C.amber, 0.06), pulse);
      return scale(C.gold, pulse);
    },

    /* fast vertical pulsing band */
    green: function (t, r, c) {
      var band = ((t * 5.2) % 1) * 9 - 0.5;
      var v = clamp01(1 - Math.abs(r - band) / 2.1);
      return mix(scale(C.green, 0.16), C.green, v * v);
    },

    /* slower vertical sweep */
    yellow: function (t, r, c) {
      var band = ((t * 1.5) % 1) * 10 - 1;
      var v = clamp01(1 - Math.abs(r - band) / 2.8);
      return mix(scale(C.yellow, 0.14), C.yellow, v);
    },

    /* directional band, left to right only — a car passing */
    blue: function (t, r, c) {
      var band = ((t * 1.9) % 1) * 10 - 1.5;
      var v = clamp01(1 - Math.abs(c - band) / 2.2);
      return mix(scale(C.blue, 0.12), C.blue, v * v);
    },

    /* one calm clean vertical sweep */
    white: function (t, r, c) {
      var band = ((t * 1.1) % 1) * 10 - 1.2;
      var v = clamp01(1 - Math.abs(r - band) / 1.9);
      return mix(scale(C.white, 0.07), C.white, v * 0.92);
    },

    /* checkerboard with a diagonal shimmer rolling across it */
    checkered: function (t, r, c) {
      var on = (((r >> 1) + (c >> 1)) % 2) === 0;
      var sh = 0.5 + 0.5 * Math.sin((r + c) * 0.9 - t * 5.0);
      if (on) return scale(C.white, 0.55 + 0.45 * sh);
      return mix(OFF, scale(C.white, 0.12), sh);
    },

    /* alternating yellow/red vertical bars, traveling gradient */
    debris: function (t, r, c) {
      var bar = ((c >> 1) % 2) === 0 ? C.yellow : C.red;
      var g = 0.5 + 0.5 * Math.sin(r * 0.7 - t * 4.2 + c * 0.35);
      return scale(bar, 0.46 + 0.54 * g);
    },

    /* slow authoritative full-matrix strobe, deep red */
    black: function (t) {
      var ph = (t * 0.9) % 1;
      var v = ph < 0.34 ? Math.sin((ph / 0.34) * Math.PI) : 0;
      return mix(OFF, C.deep, 0.25 + 0.75 * v);
    },

    /* steady breathing green — deliberately not RPM-reactive */
    idle: function (t) {
      var b = 0.5 + 0.5 * Math.sin(t * 0.85);
      return scale(C.green, 0.30 + 0.45 * b);
    },

    /* breathing blue — data feed lost */
    nosignal: function (t) {
      var b = 0.5 + 0.5 * Math.sin(t * 0.7);
      return scale(C.blue, 0.28 + 0.44 * b);
    },

    /* gear digit in red, centred, fade in / hold / fade out */
    gear: function (t, r, c, p, meta) {
      var env = p < 0.22 ? p / 0.22 : p > 0.74 ? (1 - p) / 0.26 : 1;
      if (!glyphHas(meta, r, c)) return mix(OFF, scale(C.red, 0.06), env);
      return scale(C.red, 0.40 + 0.60 * env);
    },

    /* the whole ladder — R, N, 1 through 7 — stepped through in sequence */
    gearsweep: function (t, r, c, p) {
      var n = Math.min(GEAR_ORDER.length - 1, Math.floor(p * GEAR_ORDER.length));
      var step = 1 / GEAR_ORDER.length;
      var sub = (p - n * step) / step;                  // 0..1 within this gear
      var env = sub < 0.18 ? sub / 0.18 : sub > 0.78 ? (1 - sub) / 0.22 : 1;
      if (!glyphHas(GEAR_ORDER[n], r, c)) return mix(OFF, scale(C.red, 0.06), env);
      return scale(C.red, 0.40 + 0.60 * env);
    },

    /* pit limiter — amber flash (takes priority over pit lane) */
    limiter: function (t) {
      var v = ((t * 3.4) % 1) < 0.5 ? 1 : 0.06;
      return mix(OFF, C.amber, v);
    },

    /* pit lane — red flash */
    pitlane: function (t) {
      var v = ((t * 2.6) % 1) < 0.5 ? 1 : 0.06;
      return mix(OFF, C.red, v);
    }
  };

  /* ---- timeline ----
     The dark / swell / chevron / hold effects above are still available but are
     not in the loop; add entries to LIVE to bring the ignition sequence back. */
  var LIVE = [
    { s: 'green',     d: 3.0, label: 'GREEN FLAG' },
    { s: 'yellow',    d: 3.0, label: 'YELLOW FLAG' },
    { s: 'blue',      d: 3.0, label: 'BLUE FLAG' },
    { s: 'white',     d: 2.6, label: 'WHITE FLAG' },
    { s: 'limiter',   d: 1.5, label: 'PIT LIMITER' },
    { s: 'checkered', d: 3.6, label: 'CHEQUERED FLAG' },
    { s: 'black',     d: 2.8, label: 'BLACK FLAG' },
    { s: 'gearsweep', d: 4.5, label: 'GEAR LADDER — R N 1-7' }
  ];

  /* A worked race. Shift durations are paced the way real ones feel: first and
     second go by fast, the taller gears hold longer under acceleration, and
     downshifts under braking come quick and close together. */
  function up(n, d) { return { s: 'gear', d: d, meta: n, label: 'UPSHIFT — ' + n }; }
  function down(n, d) { return { s: 'gear', d: d, meta: n, label: 'DOWNSHIFT — ' + n }; }

  var RACE = [
    { s: 'green',     d: 2.4, label: 'GREEN FLAG' },
    up('1', 0.55), up('2', 0.80), up('3', 1.05), up('4', 1.35),
    { s: 'yellow',    d: 2.4, label: 'YELLOW FLAG' },
    down('4', 0.70), down('3', 0.60), down('2', 0.50),
    { s: 'blue',      d: 2.4, label: 'BLUE FLAG' },
    up('3', 1.00), up('4', 1.25), up('5', 1.50), up('6', 1.80),
    { s: 'white',     d: 2.4, label: 'WHITE FLAG' },
    { s: 'checkered', d: 3.4, label: 'CHEQUERED FLAG' },
    down('5', 0.50), down('4', 0.45), down('3', 0.42),
    down('2', 0.40), down('1', 0.38),
    { s: 'gear',      d: 1.10, meta: 'N', label: 'NEUTRAL' }
  ];

  function mount(container, opts) {
    opts = opts || {};
    var cells = container.children;
    if (!cells || cells.length < 64) return { destroy: function () {} };

    var captionEl = opts.captionEl || null;
    var glow = opts.glowEl || null;
    var seq = LIVE.concat(opts.race === false ? [] : RACE);

    var idx = 0, stateT = 0, fade = 1, prev = null, prevT = 0, lastLabel = null;
    var buf = new Array(64);
    for (var i = 0; i < 64; i++) buf[i] = '';
    var raf = null, last = null, stopped = false;

    function step(item, t) {
      var p = clamp01(t / item.d);
      var fn = EFFECTS[item.s] || EFFECTS.dark;
      return { fn: fn, p: p, meta: item.meta };
    }

    function frame(now) {
      if (stopped) return;
      if (last === null) last = now;
      var dt = Math.min((now - last) / 1000, 0.1);
      last = now;

      var cur = seq[idx];
      stateT += dt;
      if (prev) { prevT += dt; fade = clamp01(fade + dt / CROSSFADE); }

      var a = step(cur, stateT);
      var b = prev ? step(prev, prevT) : null;

      for (var n = 0; n < 64; n++) {
        var r = (n >> 3), c = n & 7;
        var col = a.fn(stateT, r, c, a.p, a.meta);
        if (b && fade < 1) col = mix(b.fn(prevT, r, c, b.p, b.meta), col, fade);

        var rr = Math.round(255 * Math.pow(clamp01(col[0]), 1 / GAMMA));
        var gg = Math.round(255 * Math.pow(clamp01(col[1]), 1 / GAMMA));
        var bb = Math.round(255 * Math.pow(clamp01(col[2]), 1 / GAMMA));
        var lum = (col[0] * 0.3 + col[1] * 0.6 + col[2] * 0.1);
        var css = 'rgb(' + rr + ',' + gg + ',' + bb + ')';
        if (css !== buf[n]) {
          var st = cells[n].style;
          st.background = css;
          st.boxShadow = lum > 0.08 ? '0 0 ' + (6 + lum * 16).toFixed(1) + 'px rgba(' + rr + ',' + gg + ',' + bb + ',' + (0.25 + lum * 0.45).toFixed(2) + ')' : 'none';
          buf[n] = css;
        }
      }

      if (glow) {
        var mid = a.fn(stateT, 3, 3, a.p, a.meta);
        var mr = Math.round(255 * Math.pow(clamp01(mid[0]), 1 / GAMMA));
        var mg = Math.round(255 * Math.pow(clamp01(mid[1]), 1 / GAMMA));
        var mb = Math.round(255 * Math.pow(clamp01(mid[2]), 1 / GAMMA));
        glow.style.background = 'radial-gradient(circle, rgba(' + mr + ',' + mg + ',' + mb + ',0.22) 0%, rgba(' + mr + ',' + mg + ',' + mb + ',0.07) 38%, rgba(0,0,0,0) 68%)';
      }

      if (captionEl && cur.label !== lastLabel) {
        captionEl.textContent = cur.label;
        lastLabel = cur.label;
        if (opts.onState) opts.onState(cur);
      }

      if (stateT >= cur.d) {
        prev = cur; prevT = stateT; fade = 0; stateT = 0;
        idx++;
        if (idx >= seq.length) idx = 0;
      }
      raf = global.requestAnimationFrame(frame);
    }

    /* Only animate while the section is on screen — the sequence always starts
       from the top for whoever scrolls to it, and idles off screen. */
    var running = false;
    function play() {
      if (running || stopped) return;
      running = true; last = null;
      raf = global.requestAnimationFrame(frame);
    }
    function pause() {
      running = false;
      if (raf) global.cancelAnimationFrame(raf);
    }

    var io = null;
    if (global.IntersectionObserver) {
      io = new global.IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) play(); else pause();
      }, { threshold: 0.2 });
      io.observe(container);
    } else {
      play();
    }

    return {
      destroy: function () {
        stopped = true;
        pause();
        if (io) io.disconnect();
      }
    };
  }

  global.SevenThousandMatrix = {
    mount: mount, effects: EFFECTS, live: LIVE, race: RACE, gears: GEAR_ORDER
  };
})(window);